/* Overview — Admin landing page. KPIs + Recent Activity + Expiring Soon.
   Pure presentational: parent passes licenses, users, activity. */
import React from "react";
import { P } from "../../shared/constants";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import Chip from "../components/Chip";
var E = React.createElement;

/* Annual price map — kept in sync with api/license/index.js tier defaults
   (Session 49, April 2026). Edit there first; this is a UI-side derivation
   for the ARR estimate, not the source of truth. */
var ANNUAL_PRICE = { free:0, starter:499, standard:1499, enterprise:4999 };

/* Defensive numeric coercion. See Revenue.jsx for the production failure
   mode this guards against (string-typed totalPagesConverted promoting
   reducers to string concatenation). */
function asNum(v){
  if (typeof v === "number" && isFinite(v)) return v;
  var n = Number(v);
  return isFinite(n) ? n : 0;
}

function daysUntil(iso){
  if(!iso) return null;
  var ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

export default function Overview(props){
  var licenses = props.licenses || [];
  var users    = props.users    || [];
  var activity = props.activity || [];

  var active = licenses.filter(function(l){return l.status==="active"}).length;
  var activeUsers  = users.filter(function(u){return u.approved}).length;
  var pendingUsers = users.filter(function(u){return !u.approved}).length;
  var arr = licenses.reduce(function(s,l){
    return s + asNum(ANNUAL_PRICE[l.tier]);
  }, 0);
  var scansThisYear = licenses.reduce(function(s,l){
    return s + asNum(l.scansUsedThisYear);
  }, 0);

  /* Expiring within 30 days. Prefer the v1.1 expiresAt field; fall back
     to annualResetDate for licenses that haven't been migrated yet. */
  var expiringSoon = licenses.filter(function(l){
    var iso = l.expiresAt || l.annualResetDate;
    var d = daysUntil(iso);
    return d != null && d >= 0 && d <= 30;
  }).sort(function(a,b){
    return daysUntil(a.expiresAt||a.annualResetDate) - daysUntil(b.expiresAt||b.annualResetDate);
  }).slice(0, 5);

  var recent = (activity||[]).slice(0, 20);

  return E("div", {style:{display:"flex",flexDirection:"column",gap:24}},
    E("section", {"aria-labelledby":"overview-kpis-h"},
      E("h2",{id:"overview-kpis-h",style:{position:"absolute",width:1,height:1,
        padding:0,margin:-1,overflow:"hidden",clip:"rect(0,0,0,0)",border:0}},"Key metrics"),
      E("div",{style:{display:"grid",gap:12,
        gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))"}},
        E(KpiCard,{label:"Active licenses", value:active,
          color:P.priDk||P.pri, bg:P.priLt, sublabel:licenses.length+" total"}),
        E(KpiCard,{label:"Users", value:activeUsers,
          color:P.emeraldDk||P.emerald, bg:P.emeraldLt,
          sublabel:pendingUsers>0?(pendingUsers+" pending"):"all approved"}),
        E(KpiCard,{label:"ARR estimate", value:"$"+arr.toLocaleString(),
          color:P.amberDk, bg:P.amberLt,
          sublabel:"Annual recurring revenue"}),
        /* P.accent has no Dk variant in the palette today; use a darker
           cyan literal so the label clears WCAG AA on P.accentLt. */
        E(KpiCard,{label:"Scans this year", value:scansThisYear.toLocaleString(),
          color:"#155E75", bg:P.accentLt,
          sublabel:"Across all licenses"}))),

    E("section", {"aria-labelledby":"overview-expiring-h"},
      E("h2",{id:"overview-expiring-h",style:{fontSize:14,fontWeight:800,margin:"0 0 10px"}},"Expiring soon"),
      expiringSoon.length===0
        ? E(EmptyState,{title:"Nothing expiring in the next 30 days.",
            message:"Licenses renew on a rolling 12-month cycle. Check back closer to renewal dates."})
        : E("div",{style:{display:"flex",flexDirection:"column",gap:6}},
            expiringSoon.map(function(l){
              var iso = l.expiresAt || l.annualResetDate;
              var d = daysUntil(iso);
              var amber = d <= 14;
              return E("div",{key:l.domain||l.id,
                style:{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 14px",borderRadius:8,border:"1px solid "+P.bdr,
                  background:P.card}},
                E("div",null,
                  E("div",{style:{fontWeight:700,fontSize:12}}, l.orgName||l.domain),
                  E("div",{style:{fontSize:11,color:P.slateM,fontFamily:"IBM Plex Mono,monospace"}}, l.domain)),
                E("div",{style:{display:"flex",gap:8,alignItems:"center"}},
                  E(Chip,{tier:l.tier,size:"sm"}, (l.tier||"?").toUpperCase()),
                  E(Chip,{variant: amber?"amber":"slate", size:"sm"},
                    d===0?"Expires today":d===1?"1 day left":(d+" days left"))));
            }))),

    E("section", {"aria-labelledby":"overview-activity-h"},
      E("h2",{id:"overview-activity-h",style:{fontSize:14,fontWeight:800,margin:"0 0 10px"}},"Recent activity"),
      recent.length===0
        ? E(EmptyState,{title:"No activity yet.",
            message:"Admin actions, scan starts, and DocMaya conversions will appear here once the activity log is wired up."})
        : E("ol",{style:{listStyle:"none",margin:0,padding:0,
            display:"flex",flexDirection:"column",gap:4}},
            recent.map(function(a,i){
              return E("li",{key:a.id||i,
                style:{padding:"8px 12px",borderRadius:8,background:P.bgS,
                  display:"flex",justifyContent:"space-between",alignItems:"center",
                  fontSize:11,color:P.slateM}},
                E("div",null,
                  E("strong",{style:{color:P.slate}}, a.action || "event"),
                  a.targetId?E("span",null, " · ", a.targetId):null),
                E("time",{dateTime:a.createdAt||""}, a.createdAt?a.createdAt.split("T")[0]:""));
            })))
  );
}
